import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  RadioGroupItem,
} from '@parrot-software/design-system';
import { MoreVertical, Pencil, Trash2 } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';
import type { InvoiceDataViewDto } from '@subscriptions/application/dtos';
import { useFiscalRegimes } from '@subscriptions/presentation/hooks/useFiscalRegimes';
import { displayValue, getInvoiceDisplayName, getTaxRegimeLabel } from '@subscriptions/presentation/utils';

/**
 * Props for InvoiceDataCard component
 */
export interface InvoiceDataCardProps {
  areActionsDisabled?: boolean;
  invoiceData: InvoiceDataViewDto;
  isSelectable?: boolean;
  isSelected?: boolean;
  onDelete?: (invoiceData: InvoiceDataViewDto) => void;
  onEdit?: (invoiceData: InvoiceDataViewDto) => void;
  showActions?: boolean;
}

/**
 * Card displaying invoice (fiscal) data.
 * Shows a label/value grid with business name, RFC, CFDI, tax regime, email, and postal code.
 *
 * Header layout:
 * - Radio button on the left (only when `isSelectable` is true).
 * - Kebab (MoreVertical) dropdown on the right with "Editar" and "Eliminar" actions
 *   (only when `showActions` is true; `onEdit` / `onDelete` are only required in that case).
 *   `areActionsDisabled` keeps the kebab visible but disabled.
 */
export const InvoiceDataCard = ({
  areActionsDisabled = false,
  invoiceData,
  isSelectable = false,
  isSelected = false,
  onDelete,
  onEdit,
  showActions = true,
}: InvoiceDataCardProps): React.ReactElement => {
  const { t } = useTranslation('invoiceData');
  const { data: fiscalRegimes } = useFiscalRegimes();

  const cfdiLabel = t(`cfdiValues.${invoiceData.cfdi}`, { defaultValue: invoiceData.cfdi });
  const displayName = getInvoiceDisplayName(invoiceData);
  const taxRegimeLabel = getTaxRegimeLabel(invoiceData.taxRegime, fiscalRegimes);

  const fields = [
    { label: t('businessName'), value: displayValue(displayName) },
    { label: t('rfc'), value: displayValue(invoiceData.rfc) },
    { label: t('cfdi'), value: displayValue(cfdiLabel) },
    { label: t('taxRegime'), value: displayValue(taxRegimeLabel) },
    { label: t('email'), value: displayValue(invoiceData.email) },
    { label: t('postalCode'), value: displayValue(invoiceData.postalCode) },
  ];

  return (
    <div
      className={cn('flex h-full flex-col gap-5 overflow-hidden rounded-xl border p-5 shadow-xs', {
        'border-destructive': isSelected,
      })}
    >
      <div className="flex items-center justify-between">
        {isSelectable ? <RadioGroupItem checked={isSelected} className="h-4 w-4" value={invoiceData.uuid} /> : <span />}

        {showActions ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button aria-label={t('cardMenu.ariaLabel')} disabled={areActionsDisabled} size="icon" variant="outline">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit?.(invoiceData)}>
                <Pencil className="mr-2 h-4 w-4" />
                {t('editButton')}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                onClick={() => onDelete?.(invoiceData)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('deleteButton')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <span />
        )}
      </div>

      <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        {fields.map(({ label, value }) => (
          <React.Fragment key={label}>
            <span className="whitespace-nowrap font-semibold text-muted-foreground">{label}</span>
            <span className="break-all font-normal text-muted-foreground">{value}</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
